import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Lock, ArrowRight } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Reveal, SectionLabel } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HOURS_SAVED_PER_PERSON_WEEK = 4;

const fmt = (n) => n.toLocaleString("en-US", { maximumFractionDigits: 0 });

const ROICalculator = () => {
  const navigate = useNavigate();
  const [team, setTeam] = useState(10);
  const [rate, setRate] = useState(35);
  const [email, setEmail] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [sending, setSending] = useState(false);

  const hoursWeek = team * HOURS_SAVED_PER_PERSON_WEEK;
  const hoursYear = hoursWeek * 50;
  const dollarsYear = hoursYear * rate;

  const unlock = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await axios.post(`${API}/leads/roi`, { email, team_size: team, hourly_rate: rate, estimated_annual_savings: dollarsYear });
      setUnlocked(true);
      toast.success("Here's your full savings breakdown.");
    } catch {
      toast.error("Please enter a valid email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="roi-calculator" className="border-t border-zinc-900/10 bg-[#E9F4F6] py-24">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <SectionLabel>What's This Worth to You?</SectionLabel>
          <h2 className="mt-6 font-heading text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl">
            See what you're <span className="text-cyan-600">leaving on the table.</span>
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-zinc-600">
            Slide to match your business. The estimate assumes a conservative {HOURS_SAVED_PER_PERSON_WEEK} hours saved per person, per week — most owners report more.
          </p>

          <div className="mt-10 space-y-8">
            <div>
              <div className="mb-3 flex items-center justify-between">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-600">People on your team</span>
                <span data-testid="roi-team-value" className="font-heading text-2xl font-bold text-cyan-600">{team}</span>
              </div>
              <Slider data-testid="roi-team-slider" value={[team]} onValueChange={(v) => setTeam(v[0])} min={1} max={100} step={1} />
            </div>
            <div>
              <div className="mb-3 flex items-center justify-between">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-600">Average hourly cost</span>
                <span data-testid="roi-rate-value" className="font-heading text-2xl font-bold text-cyan-600">${rate}</span>
              </div>
              <Slider data-testid="roi-rate-slider" value={[rate]} onValueChange={(v) => setRate(v[0])} min={15} max={150} step={5} />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative border border-cyan-500/30 bg-cyan-500/5 p-10">
            <p className="label-mono">Your estimated savings</p>
            <div className="mt-8 space-y-6">
              <div className="flex items-baseline justify-between border-b border-zinc-900/10 pb-5">
                <span className="text-sm text-zinc-600">Hours back every week</span>
                <span data-testid="roi-hours-week" className="font-heading text-4xl font-bold text-zinc-900">{fmt(hoursWeek)}</span>
              </div>
              <div className={`space-y-6 ${unlocked ? "" : "select-none blur-md"}`}>
                <div className="flex items-baseline justify-between border-b border-zinc-900/10 pb-5">
                  <span className="text-sm text-zinc-600">Hours back every year</span>
                  <span data-testid="roi-hours-year" className="font-heading text-4xl font-bold text-zinc-900">{unlocked ? fmt(hoursYear) : "0,000"}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-zinc-600">Value recovered per year</span>
                  <span data-testid="roi-dollars-year" className="font-heading text-5xl font-bold text-cyan-600">{unlocked ? `$${fmt(dollarsYear)}` : "$000,000"}</span>
                </div>
              </div>
            </div>

            {!unlocked && (
              <form onSubmit={unlock} data-testid="roi-unlock-form" className="mt-8 border border-cyan-500/40 bg-white/70 p-6 backdrop-blur-md">
                <div className="flex items-center gap-2">
                  <Lock size={14} className="text-cyan-600" />
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-cyan-600">Unlock your full savings</p>
                </div>
                <p className="mt-2 text-sm text-zinc-600">Enter your email and we'll reveal your yearly numbers instantly.</p>
                <div className="mt-4 flex">
                  <input
                    data-testid="roi-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="work email"
                    className="w-full border border-zinc-900/20 border-r-0 bg-transparent px-4 py-3 font-mono text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-cyan-500 focus:outline-none"
                  />
                  <button data-testid="roi-unlock-btn" type="submit" disabled={sending} className="btn-primary !px-5 !py-3 shrink-0">
                    {sending ? "…" : <ArrowRight size={16} />}
                  </button>
                </div>
              </form>
            )}

            {unlocked && (
              <button data-testid="roi-cta-btn" onClick={() => navigate("/contact")} className="btn-primary mt-10 w-full">
                Stop Losing ${fmt(Math.round(dollarsYear / 52))}/Week — Book a Demo <ArrowUpRight size={16} />
              </button>
            )}
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">Estimates only · based on {HOURS_SAVED_PER_PERSON_WEEK} hrs/person/week · 50 work weeks</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default ROICalculator;
